import { CELL_ALIGNMENTS, HIGHLIGHT_COLORS, rawAttr } from "@wiki/core";
import { useRef, useState, type ReactNode } from "react";
import type { Command, EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
  deleteTable,
  distributeColumns,
  isInTable,
  mergeCells,
  moveColumn,
  moveRow,
  setCellAlignment,
  setCellBackground,
  sortColumn,
  splitCell,
  tableAt,
  tableToCsv,
  toggleHeaderColumn,
  toggleHeaderRow,
  toggleTableFullWidth,
} from "../commands/tables";
import type { EditorContext } from "../context";
import {
  MenuPopover,
  Separator,
  ToolbarButton,
  placementAtPos,
  useRovingFocus,
} from "./MenuPopover";

export interface TableToolbarsProps {
  view: EditorView;
  state: EditorState;
  context: EditorContext;
}

function columnOf(state: EditorState): number {
  const table = tableAt(state);
  if (!table) return 0;
  const $from = state.selection.$from;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    const role: unknown = node.type.spec.tableRole;
    if (role === "cell" || role === "header_cell") {
      return $from.index(depth - 1);
    }
  }
  return 0;
}

/**
 * The three table toolbars of `editor.md` §12 in one component, switched by
 * the grip the user opened: the table itself, a column, or a row. Everything
 * here is a `prosemirror-tables` command wrapped in our own naming.
 */
export function TableToolbars({ view, state, context }: TableToolbarsProps): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scope, setScope] = useState<"table" | "column" | "row">("table");
  const [swatches, setSwatches] = useState(false);
  useRovingFocus(containerRef);

  if (context.readOnly || !isInTable(state)) return null;
  const table = tableAt(state);
  if (!table) return null;

  const run = (command: Command): void => {
    command(view.state, view.dispatch, view);
    view.focus();
  };
  const column = columnOf(state);

  return (
    <MenuPopover
      placement={placementAtPos(view, table.pos)}
      role="toolbar"
      label="Table"
      testId="table-toolbar"
    >
      <div ref={containerRef} className="wiki-menu__row">
        <ToolbarButton
          label="Table options"
          active={scope === "table"}
          onSelect={() => {
            setScope("table");
          }}
        />
        <ToolbarButton
          label="Column options"
          active={scope === "column"}
          onSelect={() => {
            setScope("column");
          }}
        />
        <ToolbarButton
          label="Row options"
          active={scope === "row"}
          onSelect={() => {
            setScope("row");
          }}
        />
        <Separator />
        {scope === "table" && (
          <>
            <ToolbarButton
              label="Full width"
              active={rawAttr(table.node, "layout") === "full-width"}
              onSelect={() => {
                run(toggleTableFullWidth);
              }}
            />
            <ToolbarButton
              label="Distribute columns"
              onSelect={() => {
                run(distributeColumns);
              }}
            />
            <ToolbarButton
              label="Export as CSV"
              onSelect={() => {
                void navigator.clipboard.writeText(tableToCsv(table.node));
              }}
            />
            <Separator />
            <ToolbarButton
              label="Delete table"
              onSelect={() => {
                run(deleteTable);
              }}
            />
          </>
        )}
        {scope === "column" && (
          <>
            {CELL_ALIGNMENTS.map((alignment) => (
              <ToolbarButton
                key={alignment}
                label={`Align ${alignment}`}
                onSelect={() => {
                  run(setCellAlignment(alignment));
                }}
              />
            ))}
            <ToolbarButton
              label="Sort ascending"
              onSelect={() => {
                run(sortColumn(column, "asc"));
              }}
            />
            <ToolbarButton
              label="Sort descending"
              onSelect={() => {
                run(sortColumn(column, "desc"));
              }}
            />
            <ToolbarButton
              label="Toggle header column"
              onSelect={() => {
                run(toggleHeaderColumn);
              }}
            />
            <ToolbarButton
              label="Insert column before"
              onSelect={() => {
                run(addColumnBefore);
              }}
            />
            <ToolbarButton
              label="Insert column after"
              onSelect={() => {
                run(addColumnAfter);
              }}
            />
            <ToolbarButton
              label="Move column left"
              onSelect={() => {
                run(moveColumn(-1));
              }}
            />
            <ToolbarButton
              label="Move column right"
              onSelect={() => {
                run(moveColumn(1));
              }}
            />
            <ToolbarButton
              label="Delete column"
              onSelect={() => {
                run(deleteColumn);
              }}
            />
          </>
        )}
        {scope === "row" && (
          <>
            {CELL_ALIGNMENTS.map((alignment) => (
              <ToolbarButton
                key={alignment}
                label={`Align row ${alignment}`}
                onSelect={() => {
                  run(setCellAlignment(alignment));
                }}
              />
            ))}
            <ToolbarButton
              label="Toggle header row"
              onSelect={() => {
                run(toggleHeaderRow);
              }}
            />
            <ToolbarButton
              label="Insert row before"
              onSelect={() => {
                run(addRowBefore);
              }}
            />
            <ToolbarButton
              label="Insert row after"
              onSelect={() => {
                run(addRowAfter);
              }}
            />
            <ToolbarButton
              label="Move row up"
              onSelect={() => {
                run(moveRow(-1));
              }}
            />
            <ToolbarButton
              label="Move row down"
              onSelect={() => {
                run(moveRow(1));
              }}
            />
            <ToolbarButton
              label="Delete row"
              onSelect={() => {
                run(deleteRow);
              }}
            />
          </>
        )}
        {scope !== "table" && (
          <>
            <Separator />
            <ToolbarButton
              label="Merge cells"
              onSelect={() => {
                run(mergeCells);
              }}
            />
            <ToolbarButton
              label="Split cell"
              onSelect={() => {
                run(splitCell);
              }}
            />
            <ToolbarButton
              label="Cell background"
              active={swatches}
              onSelect={() => {
                setSwatches(!swatches);
              }}
            />
          </>
        )}
        {swatches && (
          <span className="wiki-menu__swatches" role="group" aria-label="Cell background">
            <ToolbarButton
              label="No background"
              onSelect={() => {
                run(setCellBackground(null));
                setSwatches(false);
              }}
            />
            {HIGHLIGHT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className="wiki-menu__swatch"
                style={{ backgroundColor: color }}
                aria-label={`Background ${color}`}
                data-color={color}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  run(setCellBackground(color));
                  setSwatches(false);
                }}
              />
            ))}
          </span>
        )}
      </div>
    </MenuPopover>
  );
}
